import { CISConfigType } from '../typegen/cis-config-type';
import { PaymentType } from '../typegen/payment-type';
import { PPConfigType } from '../typegen/pp-config-type';
import LanguageCode from '../types/language';
import { SupportedLanguage } from './../typegen/supported-language';

class EnumHelper {
	getSupportedLanguage(code: LanguageCode): SupportedLanguage {
		const languageCodeToEnumMap: Record<LanguageCode, SupportedLanguage> = {
			en: SupportedLanguage.English,
			es: SupportedLanguage.Spanish,
			fr: SupportedLanguage.French,
			hy: SupportedLanguage.Armenian,
			ar: SupportedLanguage.Arabic,
			zh: SupportedLanguage.Chinese,
			de: SupportedLanguage.German,
			it: SupportedLanguage.Italian,
			ru: SupportedLanguage.Russian,
			tl: SupportedLanguage.Tagalog,
			vi: SupportedLanguage.Vietnamese,
			so: SupportedLanguage.Somali,
		};

		return languageCodeToEnumMap[code] ?? SupportedLanguage.None;
	}

	/** Translation key for receipt/UI payment-type labels — resolve via getString at call sites. */
	getPaymentTypeLabelKey(paymentType: PaymentType | undefined): string {
		if (paymentType == null) return 'payment-type-unknown';

		const paymentTypeToKeyMap: Partial<Record<PaymentType, string>> = {
			[PaymentType.Cash]: 'cash',
			[PaymentType.Check]: 'check',
			[PaymentType.Card]: 'card',
			[PaymentType.CreditCard]: 'credit-label',
			[PaymentType.DebitCard]: 'debit-label',
		};
		return paymentTypeToKeyMap[paymentType] ?? 'payment-type-unknown';
	}

	getCisConfigTypeLabel(cisType: CISConfigType | undefined): string {
		const cisTypeMap: Partial<Record<CISConfigType, string>> = {
			0: 'None',
			1: 'DynaTouch CIS',
			2: 'InvoiceCloud V1',
			3: 'InvoiceCloud V2',
			4: 'ACT',
			5: 'CSA',
			6: 'Paymentus',
			7: 'ConEd',
			8: 'PayGo',
			9: 'MyUsage',
		};
		return cisTypeMap[cisType ?? 0] ?? 'Unknown CIS';
	}

	/**
	 * Label for the system that processed a cash tender, used as the payment-processor name on cash
	 * rows. Keyed by CISConfigType because PPConfigType has no CSA or ConEd member, but named the way a
	 * processor should be — note 1 is 'DynaTouch', not the CIS map's 'DynaTouch CIS', so a DynaTouch
	 * kiosk's cash rows keep reading exactly as they do today.
	 */
	getCashProcessorLabel(cisType: CISConfigType | undefined): string {
		const cashProcessorMap: Partial<Record<CISConfigType, string>> = {
			0: 'None',
			1: 'DynaTouch',
			2: 'InvoiceCloud V1',
			3: 'InvoiceCloud V2',
			4: 'ACT',
			5: 'CSA',
			6: 'Paymentus',
			7: 'ConEd',
			8: 'PayGo',
			9: 'MyUsage',
		};
		return cashProcessorMap[cisType ?? 0] ?? 'Unknown PP';
	}

	getPpConfigTypeLabel(ppType: PPConfigType | undefined): string {
		const ppTypeMap: Partial<Record<PPConfigType, string>> = {
			0: 'None',
			1: 'DynaTouch',
			2: 'InvoiceCloud V1',
			3: 'InvoiceCloud V2',
			4: 'GrantStreet',
			5: 'Fiserv',
			6: 'Paymentus',
			7: 'PayGo',
			8: 'MyUsage',
		};
		return ppTypeMap[ppType ?? 0] ?? 'Unknown PP';
	}
}

const enumHelper = new EnumHelper();
export default enumHelper;
